import { useEffect, useMemo, useState, type FormEvent } from "react";

import {
  changeMyPassword,
  chooseSubscriptionPlan,
  getCurrentUser,
  updateMyProfile,
  type UserProfile,
} from "../data/auth";

import { InternalSidebar } from "./InternalSidebar";
import { LoadingClock } from "./LoadingClock";
import { MiBoardDialog } from "./MiBoardDialog";
import { PasswordField } from "./PasswordField";

type Notice = { kind: "success" | "error"; message: string } | null;

const Item = ({
  label,
  value,
}: {
  label: string;
  value: string | number | undefined;
}) => (
  <div className="miboard-profile-field">
    <div className="miboard-profile-field__label">{label}</div>
    <div className="miboard-profile-field__value">{value || "—"}</div>
  </div>
);

const emptyProfileDraft = {
  fullName: "",
  email: "",
  cpf: "",
  phone: "",
  street: "",
  zipCode: "",
  number: "",
  neighborhood: "",
  city: "",
  state: "",
};

const emptyPasswordDraft = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

export const ProfilePage = () => {
  const [data, setData] = useState<UserProfile | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState<Notice>(null);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<
    "monthly" | "annual"
  >("monthly");
  const [selectedRenewalMode, setSelectedRenewalMode] = useState<
    "automatic" | "manual"
  >("manual");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingSubscription, setSavingSubscription] = useState(false);
  const [profileDraft, setProfileDraft] = useState(emptyProfileDraft);
  const [passwordDraft, setPasswordDraft] = useState(emptyPasswordDraft);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlHeight = html.style.height;
    const previousBodyHeight = body.style.height;

    html.style.overflow = "auto";
    body.style.overflow = "auto";
    html.style.height = "auto";
    body.style.height = "auto";

    getCurrentUser()
      .then((value) => {
        setData(value);
        setProfileDraft({
          fullName: value.profile.fullName || value.profile.name,
          email: value.profile.email,
          cpf: value.profile.cpf || "",
          phone: value.profile.phone || "",
          street: value.profile.street || "",
          zipCode: value.profile.zipCode || "",
          number: value.profile.number || "",
          neighborhood: value.profile.neighborhood || "",
          city: value.profile.city || "",
          state: value.profile.state || "",
        });
        setSelectedPlanId(
          value.subscription.planId ||
            value.subscription.availablePlans[0]?.id ||
            "",
        );
        setSelectedBillingCycle(
          value.subscription.billingCycle === "Anual" ? "annual" : "monthly",
        );
        setSelectedRenewalMode(value.subscription.renewalMode || "manual");
      })
      .catch((reason) => {
        setError(
          reason instanceof Error
            ? reason.message
            : "Não foi possível carregar o perfil.",
        );
      })
      .finally(() => setLoading(false));

    return () => {
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
      html.style.height = previousHtmlHeight;
      body.style.height = previousBodyHeight;
    };
  }, []);

  const currentPlan = useMemo(() => {
    if (!data) {
      return null;
    }
    const availablePlans = data.subscription.availablePlans || [];
    return (
      availablePlans.find((plan) => plan.id === data.subscription.planId) ||
      availablePlans.find((plan) => plan.code === data.subscription.planCode) ||
      null
    );
  }, [data]);

  const availablePlans = useMemo(() => {
    if (!data) {
      return [];
    }
    return (data.subscription.availablePlans || []).filter(
      (plan) => plan.position >= data.subscription.currentPlanPosition,
    );
  }, [data]);

  const handleSaveProfile = async (event: FormEvent) => {
    event.preventDefault();
    setSavingProfile(true);
    setNotice(null);
    setError("");
    try {
      const updated = await updateMyProfile(profileDraft);
      setData((current) =>
        current
          ? {
              ...current,
              profile: {
                ...current.profile,
                ...updated.profile,
                fullName: updated.profile.fullName || updated.profile.name,
              },
            }
          : current,
      );
      setEditingProfile(false);
      setNotice({
        kind: "success",
        message: "Dados do perfil atualizados com sucesso.",
      });
    } catch (reason) {
      setNotice({
        kind: "error",
        message:
          reason instanceof Error
            ? reason.message
            : "Não foi possível salvar o perfil.",
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (event: FormEvent) => {
    event.preventDefault();
    setSavingPassword(true);
    setNotice(null);
    setError("");
    if (passwordDraft.newPassword !== passwordDraft.confirmPassword) {
      setNotice({
        kind: "error",
        message: "A nova senha e a confirmação precisam ser iguais.",
      });
      setSavingPassword(false);
      return;
    }
    try {
      await changeMyPassword(
        passwordDraft.currentPassword,
        passwordDraft.newPassword,
      );
      setPasswordDraft(emptyPasswordDraft);
      setNotice({
        kind: "success",
        message: "Senha alterada com sucesso.",
      });
    } catch (reason) {
      setNotice({
        kind: "error",
        message:
          reason instanceof Error
            ? reason.message
            : "Não foi possível alterar a senha.",
      });
    } finally {
      setSavingPassword(false);
    }
  };

  const handleChoosePlan = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedPlanId) {
      setNotice({
        kind: "error",
        message: "Selecione um plano antes de continuar.",
      });
      return;
    }
    setSavingSubscription(true);
    setNotice(null);
    setError("");
    try {
      const result = await chooseSubscriptionPlan(
        selectedPlanId,
        selectedBillingCycle,
        selectedRenewalMode,
      );
      if (result.checkout?.link) {
        window.location.assign(result.checkout.link);
        return;
      }
      setBillingDialogOpen(false);
      setNotice({
        kind: "success",
        message:
          selectedRenewalMode === "automatic"
            ? selectedPlanId === currentPlan?.id
              ? "Renovação automática iniciada."
              : "Upgrade automático iniciado."
            : selectedPlanId === currentPlan?.id
            ? "Renovação manual registrada com sucesso."
            : "Upgrade manual registrado com sucesso.",
      });
    } catch (reason) {
      setNotice({
        kind: "error",
        message:
          reason instanceof Error
            ? reason.message
            : "Não foi possível atualizar a assinatura.",
      });
    } finally {
      setSavingSubscription(false);
    }
  };

  if (loading) {
    return (
      <main className="miboard-loading-screen">
        <LoadingClock label="Carregando perfil..." />
      </main>
    );
  }

  return (
    <main className="miboard-profile-shell">
      <div className="miboard-profile-layout">
        <InternalSidebar active="profile" />
        <div className="miboard-profile-content">
          <img
            src="/image/Logo%20Miboard.png"
            alt="MiBoard"
            className="miboard-profile-logo"
          />

          <div className="miboard-profile-header">
            <div>
              <h1 className="miboard-profile-title">Perfil</h1>
              <p className="miboard-profile-subtitle">
                Tudo o que importa sobre a conta, endereço e assinatura.
              </p>
            </div>
          </div>

          {(error || notice) && (
            <section
              className="miboard-profile-card"
              style={{
                borderColor: notice
                  ? notice.kind === "success"
                    ? "rgba(22, 125, 80, 0.25)"
                    : "rgba(180, 35, 24, 0.28)"
                  : undefined,
              }}
            >
              <p
                className={
                  notice?.kind === "error"
                    ? "miboard-profile-error"
                    : "miboard-profile-muted"
                }
                style={{ margin: 0 }}
              >
                {error || notice?.message}
              </p>
            </section>
          )}

          {data && (
            <div className="miboard-profile-sections">
              <section className="miboard-profile-card">
                <div className="miboard-profile-cardHeader">
                  <h2 className="miboard-profile-card-title">Conta</h2>
                  {!editingProfile && (
                    <button
                      type="button"
                      onClick={() => setEditingProfile(true)}
                      className="miboard-profile-button miboard-profile-button--primary"
                    >
                      Editar dados
                    </button>
                  )}
                </div>
                {editingProfile ? (
                  <form
                    className="miboard-profile-form"
                    onSubmit={handleSaveProfile}
                  >
                    <div className="miboard-profile-formGrid">
                      <label className="miboard-profile-fieldGroup">
                        <span className="miboard-profile-field__label">
                          Nome completo
                        </span>
                        <input
                          className="miboard-profile-control"
                          value={profileDraft.fullName}
                          onChange={(event) =>
                            setProfileDraft((current) => ({
                              ...current,
                              fullName: event.target.value,
                            }))
                          }
                          required
                        />
                      </label>
                      <label className="miboard-profile-fieldGroup">
                        <span className="miboard-profile-field__label">
                          E-mail
                        </span>
                        <input
                          className="miboard-profile-control"
                          value={profileDraft.email}
                          onChange={(event) =>
                            setProfileDraft((current) => ({
                              ...current,
                              email: event.target.value,
                            }))
                          }
                          type="email"
                          required
                        />
                      </label>
                      <label className="miboard-profile-fieldGroup">
                        <span className="miboard-profile-field__label">
                          CPF
                        </span>
                        <input
                          className="miboard-profile-control"
                          value={profileDraft.cpf}
                          onChange={(event) =>
                            setProfileDraft((current) => ({
                              ...current,
                              cpf: event.target.value,
                            }))
                          }
                          placeholder="000.000.000-00"
                        />
                      </label>
                      <label className="miboard-profile-fieldGroup">
                        <span className="miboard-profile-field__label">
                          Telefone
                        </span>
                        <input
                          className="miboard-profile-control"
                          value={profileDraft.phone}
                          onChange={(event) =>
                            setProfileDraft((current) => ({
                              ...current,
                              phone: event.target.value,
                            }))
                          }
                          placeholder="(00) 00000-0000"
                        />
                      </label>
                    </div>

                    <div className="miboard-profile-card miboard-profile-card--soft">
                      <h3 className="miboard-profile-subcardTitle">Endereço</h3>
                      <div className="miboard-profile-formGrid">
                        <label className="miboard-profile-fieldGroup">
                          <span className="miboard-profile-field__label">
                            Logradouro
                          </span>
                          <input
                            className="miboard-profile-control"
                            value={profileDraft.street}
                            onChange={(event) =>
                              setProfileDraft((current) => ({
                                ...current,
                                street: event.target.value,
                              }))
                            }
                          />
                        </label>
                        <label className="miboard-profile-fieldGroup">
                          <span className="miboard-profile-field__label">
                            CEP
                          </span>
                          <input
                            className="miboard-profile-control"
                            value={profileDraft.zipCode}
                            onChange={(event) =>
                              setProfileDraft((current) => ({
                                ...current,
                                zipCode: event.target.value,
                              }))
                            }
                            placeholder="00000-000"
                          />
                        </label>
                        <label className="miboard-profile-fieldGroup">
                          <span className="miboard-profile-field__label">
                            Nº
                          </span>
                          <input
                            className="miboard-profile-control"
                            value={profileDraft.number}
                            onChange={(event) =>
                              setProfileDraft((current) => ({
                                ...current,
                                number: event.target.value,
                              }))
                            }
                          />
                        </label>
                        <label className="miboard-profile-fieldGroup">
                          <span className="miboard-profile-field__label">
                            Bairro
                          </span>
                          <input
                            className="miboard-profile-control"
                            value={profileDraft.neighborhood}
                            onChange={(event) =>
                              setProfileDraft((current) => ({
                                ...current,
                                neighborhood: event.target.value,
                              }))
                            }
                          />
                        </label>
                        <label className="miboard-profile-fieldGroup">
                          <span className="miboard-profile-field__label">
                            Cidade
                          </span>
                          <input
                            className="miboard-profile-control"
                            value={profileDraft.city}
                            onChange={(event) =>
                              setProfileDraft((current) => ({
                                ...current,
                                city: event.target.value,
                              }))
                            }
                          />
                        </label>
                        <label className="miboard-profile-fieldGroup">
                          <span className="miboard-profile-field__label">
                            Estado
                          </span>
                          <input
                            className="miboard-profile-control"
                            value={profileDraft.state}
                            onChange={(event) =>
                              setProfileDraft((current) => ({
                                ...current,
                                state: event.target.value,
                              }))
                            }
                          />
                        </label>
                      </div>
                    </div>

                    <div className="miboard-profile-actionsRow">
                      <button
                        type="submit"
                        className="miboard-profile-button miboard-profile-button--primary"
                        disabled={savingProfile}
                      >
                        {savingProfile ? "Salvando..." : "Salvar alterações"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingProfile(false)}
                        className="miboard-cancel-button"
                      >
                        Cancelar
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="miboard-profile-details">
                    <Item label="Nome completo" value={data.profile.fullName} />
                    <Item label="E-mail" value={data.profile.email} />
                    <Item label="CPF" value={data.profile.cpf} />
                    <Item label="Telefone" value={data.profile.phone} />
                    <Item label="Logradouro" value={data.profile.street} />
                    <Item label="CEP" value={data.profile.zipCode} />
                    <Item label="Nº" value={data.profile.number} />
                    <Item label="Bairro" value={data.profile.neighborhood} />
                    <Item label="Cidade" value={data.profile.city} />
                    <Item label="Estado" value={data.profile.state} />
                    <Item
                      label="Membro desde"
                      value={new Date(
                        data.profile.memberSince,
                      ).toLocaleDateString("pt-BR")}
                    />
                  </div>
                )}
              </section>

              <section className="miboard-profile-card">
                <h2 className="miboard-profile-card-title">Segurança</h2>
                <form
                  className="miboard-profile-form"
                  onSubmit={handleChangePassword}
                >
                  <div className="miboard-profile-formGrid">
                    <label className="miboard-profile-fieldGroup">
                      <span className="miboard-profile-field__label">
                        Senha atual
                      </span>
                      <PasswordField
                        inputClassName="miboard-profile-control"
                        value={passwordDraft.currentPassword}
                        onChange={(event) =>
                          setPasswordDraft((current) => ({
                            ...current,
                            currentPassword: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="miboard-profile-fieldGroup">
                      <span className="miboard-profile-field__label">
                        Nova senha
                      </span>
                      <PasswordField
                        inputClassName="miboard-profile-control"
                        value={passwordDraft.newPassword}
                        onChange={(event) =>
                          setPasswordDraft((current) => ({
                            ...current,
                            newPassword: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="miboard-profile-fieldGroup">
                      <span className="miboard-profile-field__label">
                        Confirmar nova senha
                      </span>
                      <PasswordField
                        inputClassName="miboard-profile-control"
                        value={passwordDraft.confirmPassword}
                        onChange={(event) =>
                          setPasswordDraft((current) => ({
                            ...current,
                            confirmPassword: event.target.value,
                          }))
                        }
                      />
                    </label>
                  </div>
                  <div className="miboard-profile-actionsRow">
                    <button
                      type="submit"
                      className="miboard-profile-button miboard-profile-button--primary"
                      disabled={savingPassword}
                    >
                      {savingPassword ? "Atualizando..." : "Trocar senha"}
                    </button>
                  </div>
                </form>
              </section>

              <section className="miboard-profile-card">
                <h2 className="miboard-profile-card-title">Assinatura</h2>
                <div className="miboard-profile-details">
                  <Item
                    label="Plano atual"
                    value={data.subscription.planName}
                  />
                  <Item label="Status" value={data.subscription.status} />
                  <Item label="Ciclo" value={data.subscription.billingCycle} />
                  <Item label="IA" value={data.subscription.aiAccess} />
                  <Item
                    label="Armazenamento"
                    value={data.subscription.storage}
                  />
                  <Item label="Assentos" value={data.subscription.seats} />
                </div>
              </section>

              <section className="miboard-profile-card">
                <h2 className="miboard-profile-card-title">Limites e uso</h2>
                <div className="miboard-profile-details">
                  <Item
                    label="Limite de painéis"
                    value={data.subscription.boardLimit}
                  />
                  <Item
                    label="Uso atual"
                    value={data.subscription.boardUsage}
                  />
                  <Item
                    label="Limite de workspaces"
                    value={data.subscription.workspaceLimit}
                  />
                </div>
              </section>

              <section className="miboard-profile-card">
                <h2 className="miboard-profile-card-title">
                  Cobrança e suporte
                </h2>
                <div className="miboard-profile-details">
                  <Item label="Suporte" value={data.subscription.support} />
                  <Item
                    label="Próxima renovação"
                    value={data.subscription.nextRenewal}
                  />
                  <Item label="Upgrade" value={data.subscription.upgradeHint} />
                </div>
                <div className="miboard-profile-actionsRow">
                  <button
                    type="button"
                    className="miboard-profile-button miboard-profile-button--primary"
                    onClick={() => setBillingDialogOpen(true)}
                  >
                    Renovar ou fazer upgrade
                  </button>
                </div>
              </section>
            </div>
          )}
        </div>
      </div>

      {billingDialogOpen && data && (
        <MiBoardDialog
          title="Renovar ou fazer upgrade"
          onClose={() => setBillingDialogOpen(false)}
          actions={
            <>
              <button
                type="button"
                className="miboard-cancel-button"
                onClick={() => setBillingDialogOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="miboard-save-button"
                disabled={savingSubscription}
                onClick={() => {
                  const form = document.getElementById(
                    "miboard-billing-form",
                  ) as HTMLFormElement | null;
                  form?.requestSubmit();
                }}
              >
                {savingSubscription ? "Salvando..." : "Confirmar"}
              </button>
            </>
          }
        >
          <form
            id="miboard-billing-form"
            className="miboard-profile-form"
            onSubmit={handleChoosePlan}
          >
            <p className="miboard-profile-muted" style={{ marginTop: 0 }}>
              Selecione um plano igual ao atual para renovar ou um acima para
              fazer upgrade.
            </p>
            <div className="miboard-profile-planGrid">
              {availablePlans.map((plan) => {
                const isSelected = selectedPlanId === plan.id;
                const monthly = (plan.monthlyPriceCents / 100).toFixed(2);
                const annual = (plan.annualPriceCents / 100).toFixed(2);
                return (
                  <button
                    key={plan.id}
                    type="button"
                    className={`miboard-profile-planCard${
                      isSelected ? " miboard-profile-planCard--selected" : ""
                    }`}
                    onClick={() => setSelectedPlanId(plan.id)}
                  >
                    <strong>{plan.name}</strong>
                    <small>{plan.description}</small>
                    <span>
                      R$ {monthly}/mês · R$ {annual}/ano
                    </span>
                  </button>
                );
              })}
            </div>
            <label className="miboard-profile-fieldGroup">
              <span className="miboard-profile-field__label">
                Periodicidade
              </span>
              <select
                className="miboard-profile-control"
                value={selectedBillingCycle}
                onChange={(event) =>
                  setSelectedBillingCycle(
                    event.target.value === "annual" ? "annual" : "monthly",
                  )
                }
              >
                <option value="monthly">Mensal</option>
                <option value="annual">Anual</option>
              </select>
            </label>
            <label className="miboard-profile-fieldGroup">
              <span className="miboard-profile-field__label">
                Forma de renovação
              </span>
              <select
                className="miboard-profile-control"
                value={selectedRenewalMode}
                onChange={(event) =>
                  setSelectedRenewalMode(
                    event.target.value === "automatic" ? "automatic" : "manual",
                  )
                }
              >
                <option value="automatic">Automática por cartão</option>
                <option value="manual">Manual com novo pagamento</option>
              </select>
            </label>
            <p className="miboard-profile-muted">
              Renovação automática usa assinatura recorrente com cartão.
              Renovação manual cria uma cobrança avulsa e o usuário paga
              novamente quando renovar.
            </p>
            <p className="miboard-profile-muted">
              A próxima renovação atualizada no banco será calculada
              automaticamente após confirmar.
            </p>
          </form>
        </MiBoardDialog>
      )}
    </main>
  );
};
